import { Flex } from 'reflexbox';
import { BatteryIcon, InfoIcon, UtilityIcon } from '~/icons';
import { colors } from '~/styles/colors';
import { IconWrapper, IconWrapperBig, IconWrapperMini } from './styles';
import {
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, LineChart, Tooltip, Line,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { getFormattedTimeFromHour, timeFormater, getDateFromHour } from '~/helpers/formatTime';
import { useStateVar } from '~/helpers/useStateVar';
import { ContainerGraphicIndexUsage, TextPNoMargin } from '../../DALs/styles';
import { ContainerCardDmtUsage } from '../styles';
import { acessorUtilities } from '../AcessorUtilities';

export function resize(func: () => void) {
  document.body.onresize = function () {
    func();
  };
}

const LabelFormater = (utilityType, value: number) => (
  <>
    {`${acessorUtilities[utilityType]?.states[value]?.join(' ') || ''}`}
  </>
);

const UtilityCustomTick = (props) => {
  const {
    x, y, payload, anchor, utilityType,
  } = props;
  const labelRaw = acessorUtilities[utilityType]?.states[payload.value] || 'teste';

  return (
    <g transform={`translate(${x - 3},${anchor ? y : y - 12})`}>
      <text x={0} y={0} dy={16} textAnchor={anchor || 'end'} fill="#666" fontSize="11px" style={{ maxWidth: 14, textTransform: 'capitalize' }}>
        {labelRaw.map((line, index) => (
          <tspan key={line} x={0} dy={index === 0 ? 16 : 12}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const CustomXAxisTick = (props) => {
  const {
    x, y, payload, index, numDays, dateStart,
  } = props;
  const isFirst = index === 0;
  const textAnchor = isFirst ? 'start' : 'middle';
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={8}
        textAnchor={textAnchor}
        fontSize="11px"
      >
        {numDays && numDays > 1 ? getDateFromHour(payload.value, dateStart) : getFormattedTimeFromHour(payload.value)}
      </text>
    </g>
  );
};

export const DmtUtilityHistoryCard = (props: {dateStart: string, timeRange: string, timeUsingBattery: number, numDays: number, y: number[], commonX: number[], xTicks: number[], xDomain: number[], dmtCode: string, timeByStatus,
  name: string,
  datCode?: number, consumption?: number | null, utilityType: string }): JSX.Element => {
  const { t } = useTranslation();
  const [state, _render, setState] = useStateVar({
    widthPageCard: window.innerWidth,
  });
  resize(() => setState({ widthPageCard: document.body.clientWidth }));
  const customTick = (tickProps: any) => {
    const {
      x, y, payload, index,
    } = tickProps;
    const { value } = payload;
    const isFirst = index === 0;
    const textAnchor = isFirst ? 'start' : 'middle';
    return <text x={x} y={y - 4} textAnchor={textAnchor} className="recharts-text">{`${getFormattedTimeFromHour(value)}`}</text>;
  };

  const getVisibleXTicks = (ticks: number[]) => {
    const width = state.widthPageCard;

    let interval = 1;
    if (width < 630 && props.numDays > 7) interval = 6;
    else if (width < 400 && props.numDays > 7) interval = 10;
    else if (width < 400) interval = 6;
    else if (width < 800) interval = 3;
    else if (width < 987) interval = 2;

    return ticks.filter((_, index) => index % interval === 0);
  };

  const visibleXTicks = getVisibleXTicks(props.xTicks);
  return (
    <ContainerGraphicIndexUsage>
      <ContainerCardDmtUsage>
        <Flex flexDirection="row" justifyContent="space-between" alignItems="flex-start">
          <IconWrapper>
            {acessorUtilities[props.utilityType]?.icon}
          </IconWrapper>
          <Flex flexDirection="column">
            <span style={{ fontWeight: 'bold', marginBottom: 5 }}>
              {props.name || '-'}
            </span>
            {props.utilityType && (
              <span style={{ color: colors.Grey300, fontSize: 12, marginTop: 3 }}>
                {`${props.datCode || '-'}`}
              </span>
            )}
          </Flex>
        </Flex>
        {acessorUtilities[props.utilityType]?.usageIndex && (
          <>
            <CalcTimeByStatus label={acessorUtilities[props.utilityType]?.usageIndex[2]?.label} icon={acessorUtilities[props.utilityType]?.usageIndex[2]?.icon} value={props.timeByStatus?.formattedTimeByStatus[2]} numDays={props.numDays} />
            <CalcTimeByStatus label={acessorUtilities[props.utilityType]?.usageIndex[1]?.label} icon={acessorUtilities[props.utilityType]?.usageIndex[1]?.icon} value={props.timeByStatus?.formattedTimeByStatus[1]} numDays={props.numDays} />
            <CalcTimeByStatus label={acessorUtilities[props.utilityType]?.usageIndex[0]?.label} icon={acessorUtilities[props.utilityType]?.usageIndex[0]?.icon} value={props.timeByStatus?.formattedTimeByStatus[0]} numDays={props.numDays} />
            <CalcTimeByStatus label={acessorUtilities[props.utilityType]?.usageIndex['-1']?.label} icon={acessorUtilities[props.utilityType]?.usageIndex['-1']?.icon} value={props.timeByStatus?.formattedTimeByStatus['-1']} numDays={props.numDays} />
          </>
        )}
      </ContainerCardDmtUsage>
      <Flex flexDirection="column" alignItems="flex-start" justifyContent="center">
        <TextPNoMargin>{t('tempoDeUso')}</TextPNoMargin>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart
            height={180}
            margin={{
              top: 10,
              right: state.widthPageCard > 767 ? 30 : 5,
              left: state.widthPageCard > 767 ? 30 : 18,
              bottom: 0,
            }}
            data={props.commonX.map((v, i) => ({ x: v, y: props.y.length > i ? props.y[i] : null }))}
          >
            <CartesianGrid strokeDasharray="3 3" />

            <Tooltip isAnimationActive={false} cursor={{ stroke: colors.Grey200, strokeWidth: 1 }} labelFormatter={(hour) => timeFormater(hour, props.dateStart)} formatter={(value) => LabelFormater(props.utilityType, value)} />

            <Line name={acessorUtilities[props.utilityType].namePt} yAxisId={props.utilityType} type="stepAfter" dataKey="y" dot={false} stroke={colors.BlueSecondary} strokeWidth={2.5} animationDuration={300} isAnimationActive={false} strokeDasharray="" key={`line-${props.utilityType}`} />

            <YAxis type="number" yAxisId={props.utilityType} allowDataOverflow tick={<UtilityCustomTick utilityType={props.utilityType} />} ticks={acessorUtilities[props.utilityType]?.ticks} interval={0} domain={acessorUtilities[props.utilityType]?.domain} />
            {props.numDays && props.numDays > 1 ? (<XAxis allowDataOverflow xAxisId={props.utilityType} tickLine={false} axisLine={false} allowDuplicatedCategory={false} tick={customTick} type="number" dataKey="x" ticks={visibleXTicks} domain={props.xDomain} />) : null}
            <XAxis allowDataOverflow type="number" dataKey="x" tick={<CustomXAxisTick numDays={props.numDays} dateStart={props.dateStart} />} ticks={visibleXTicks} domain={props.xDomain} />

          </LineChart>
        </ResponsiveContainer>
      </Flex>
    </ContainerGraphicIndexUsage>
  );
};

const CalcTimeByStatus = ({
  label, value, icon, numDays,
}) => (
  <>
    {label && (
      <Flex flexDirection="row" justifyContent="space-between" alignItems="flex-start">
        <IconWrapperBig>
          {icon}
        </IconWrapperBig>
        <Flex flexDirection="column">
          <span style={{ fontWeight: 'bold', marginBottom: 5 }}>
            {label}
          </span>
          <p style={{ margin: 0, color: colors.text['sub-500'] }}>
            {`
              ${numDays > 7 && value && value !== '00:00' ? '≤' : ''}
              ${numDays === 7 && value && value !== '00:00' ? '≥' : ''}
              ${value || '00:00'} ${t('horas')}
            `}
          </p>
        </Flex>
      </Flex>
    )}
  </>
);
